import { medicationRequest } from "./utils/medicationRequest";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Pill, Plus, X, Clock, Check, AlertCircle, Camera, Trash2,
  Calendar, Utensils, Bell, TrendingUp, Sparkles,
  AlertTriangle, Volume2, VolumeX, Loader2,
  Activity, Send, Bot, User, FileText, ShieldAlert,
} from "lucide-react";
import { supabase } from "./supabase";
import type { Session } from "@supabase/supabase-js";
import { MedicationAssistant } from "./components/MedicationAssistant";
import { checkCreditsForAction, getCreditsForEndpoint } from "./hooks/useCredits";

import { localDateKey, treatmentEnd, isTreatmentActive, scheduledDate, dayBounds, validateSchedule, reminderStage } from "./utils/medicationSchedule";

type Lang = "ar" | "en";
type Dir = "rtl" | "ltr";

interface Medication {
  id: string;
  user_id: string;
  name: string;
  dosage: string;
  frequency: string;
  times: string[];
  food_relation: string;
  duration_value: number;
  duration_unit: string;
  start_date: string;
  end_date: string;
  photo_url: string | null;
  ai_advice: { advice?: string[]; interactionWarning?: string | null } | null;
  interaction_warning: string | null;
  is_active: boolean;
  created_at: string;
  medication_type: string;
  stock_count: number | null;
  low_stock_threshold: number | null;
  doctor_notes: string | null;
}

interface DoseLog {
  id: string;
  medication_id: string;
  scheduled_time: string;
  status: string;
  taken_at: string | null;
}

const tr = (ar: string, en: string, lang: Lang) => (lang === "ar" ? ar : en);

const FREQUENCY_OPTIONS = [
  { value: "once", timesCount: 1, ar: "مرة واحدة يومياً", en: "Once daily" },
  { value: "twice", timesCount: 2, ar: "مرتين يومياً", en: "Twice daily" },
  { value: "thrice", timesCount: 3, ar: "ثلاث مرات يومياً", en: "Three times daily" },
];

const FOOD_OPTIONS = [
  { value: "before", ar: "قبل الطعام", en: "Before food" },
  { value: "after", ar: "بعد الطعام", en: "After food" },
  { value: "with", ar: "مع الطعام", en: "With food" },
  { value: "any", ar: "أي وقت", en: "Anytime" },
];

const DURATION_UNITS = [
  { value: "days", ar: "أيام", en: "days" },
  { value: "weeks", ar: "أسابيع", en: "weeks" },
];

const MED_TYPES = [
  { value: "pills", ar: "حبوب/كبسولات", en: "Pills/Capsules" },
  { value: "syrup", ar: "شراب", en: "Syrup" },
  { value: "drops", ar: "قطرات", en: "Drops" },
  { value: "spray", ar: "بخاخ", en: "Spray" },
  { value: "injection", ar: "حقنة", en: "Injection" },
  { value: "ointment", ar: "مرهم/كريم", en: "Ointment/Cream" },
];

function getUnitLabel(type: string, lang: Lang): string {
  const isAr = lang === "ar";
  switch (type) {
    case "pills": return isAr ? "ملغ / حبة" : "mg / Tablets";
    case "syrup": return isAr ? "مل" : "ml";
    case "drops": return isAr ? "قطرة" : "Drops";
    case "spray": return isAr ? "بخة" : "Sprays";
    case "injection": return isAr ? "مل / وحدة" : "ml / Units";
    case "ointment": return isAr ? "تطبيق / طبقة" : "Application / Layer";
    default: return isAr ? "جرعة" : "Dosage";
  }
}

function getRemainingDays(endDate: string): number {
  const end = new Date(`${endDate}T12:00:00`);
  const now = new Date();
  end.setHours(23, 59, 59, 999);
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}



/**
 * Medicine photos live in a private bucket. Rows store the object path, so the image
 * is displayed through a short-lived signed URL that only its owner can obtain.
 */
function MedPhoto({ path, className, alt }: { path: string; className?: string; alt?: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (path.startsWith("blob:") || path.startsWith("data:")) {
      setUrl(path);
      return;
    }
    const objectPath = path.includes("/medication-photos/")
      ? path.split("/medication-photos/")[1]
      : path;
    supabase.storage
      .from("medication-photos")
      .createSignedUrl(objectPath, 3600)
      .then(({ data }) => { if (!cancelled) setUrl(data?.signedUrl ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [path]);

  if (!url) return null;
  return <img src={url} alt={alt ?? ""} className={className} />;
}

export function MedicationTab({ session, dir, lang: langProp, onSignInRequest, refreshCredits }: { session: Session | null; dir: Dir; lang: Lang; onSignInRequest?: () => void; refreshCredits: () => void | Promise<void> }) {
  const lang = langProp;
  const isAr = lang === "ar";
  const [medications, setMedications] = useState<Medication[]>([]);
  const [doseLogs, setDoseLogs] = useState<DoseLog[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notificationStatus, setNotificationStatus] = useState("");
  const savingRef = useRef(false);
  const doseBusy = useRef(new Set<string>());
  const reminderSent = useRef(new Set<string>());
  const [loudAlerts, setLoudAlerts] = useState(() => localStorage.getItem("loud_alerts_seniors") === "true");
  const [showAssistant, setShowAssistant] = useState(false);

  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // Form state
  const [name, setName] = useState("");
  const [dosage, setDosage] = useState("");
  const [frequency, setFrequency] = useState("once");
  const [times, setTimes] = useState<string[]>(["08:00"]);
  const [foodRelation, setFoodRelation] = useState("any");
  const [durationValue, setDurationValue] = useState(7);
  const [durationUnit, setDurationUnit] = useState("days");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [medType, setMedType] = useState("pills");
  const [stockCount, setStockCount] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("5");
  const [doctorNotes, setDoctorNotes] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const userId = session?.user?.id;

  const loadMedications = useCallback(async () => {
    if (!userId) { setMedications([]); setDoseLogs([]); setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from("medications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) {
      setFormError(langProp === "ar" ? "تعذر تحميل الأدوية؛ حاول مجددًا." : "Could not load medications; please retry.");
      setLoading(false); return;
    }
    setMedications((data || []) as Medication[]);

    const [dayStart, dayEnd] = dayBounds();
    const { data: logs } = await supabase
      .from("dose_logs")
      .select("*")
      .eq("user_id", userId)
      .gte("scheduled_time", dayStart)
      .lt("scheduled_time", dayEnd);
    setDoseLogs((logs || []) as DoseLog[]);
    setLoading(false);
  }, [userId, langProp]);

  useEffect(() => { loadMedications(); }, [loadMedications]);

  // Web reminders are best-effort while the page remains open. Never claim background delivery.
  useEffect(() => {
    const check = () => {
      const now = new Date();
      medications.forEach(med => {
        if (!isTreatmentActive(med, now)) return;
        med.times.forEach(time => {
          const scheduled = scheduledDate(time, now).toISOString();
          if (doseLogs.some(log => log.medication_id === med.id && log.scheduled_time === scheduled && log.status === "taken")) return;
          const stage = reminderStage(time, now);
          const key = [userId, med.id, scheduled, stage].join(":");
          if (!stage || reminderSent.current.has(key)) return;
          reminderSent.current.add(key);
          sendNotification(stage === "soon"
            ? (isAr ? `تذكير: ${med.name} بعد 10 دقائق` : `Reminder: ${med.name} in 10 minutes`)
            : (isAr ? `موعد مسجل: ${med.name}` : `Scheduled dose: ${med.name}`), loudAlerts);
        });
      });
    };
    check();
    const timer = setInterval(check, 15000);
    return () => clearInterval(timer);
  }, [medications, doseLogs, userId, loudAlerts, isAr]);

  // Refresh date-scoped records across midnight and when returning to the page.
  useEffect(() => {
    let day = localDateKey();
    const timer = setInterval(() => {
      if (localDateKey() !== day) { day = localDateKey(); void loadMedications(); }
    }, 30000);
    const onFocus = () => { void loadMedications(); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, [loadMedications]);

  const sendChatMessage = async () => {
    const q = chatInput.trim();
    if (!q || chatLoading) return;
    const userMsg = { role: "user" as const, content: q };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput("");
    setChatLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const creditCheck = await checkCreditsForAction(getCreditsForEndpoint("medication-chat"));
      if (!creditCheck.allowed) {
        setChatMessages(prev => [...prev, { role: "assistant", content: isAr ? "ليس لديك رصيد كافٍ. اشترك للحصول على المزيد." : "Not enough credits. Subscribe for more." }]);
        setChatLoading(false);
        return;
      }
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/medication-chat`;
      const res = await medicationRequest(apiUrl, session?.access_token, {
          question: q,
          medications: medications.map(m => ({ name: m.name, dosage: m.dosage, frequency: m.frequency, food_relation: m.food_relation, times: m.times })),
          history: chatMessages,
          lang,
        }, refreshCredits);
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, { role: "assistant", content: data.answer || (isAr ? "عذراً، لم أتمكن من الإجابة" : "Sorry, I couldn't answer") }]);
      } else if (res.status === 402) {
        setChatMessages(prev => [...prev, { role: "assistant", content: isAr ? "ليس لديك رصيد كافٍ" : "Not enough credits" }]);
      } else {
        let errDetail = "";
        try { const errData = await res.json(); errDetail = errData.error || ""; } catch { /* ignore */ }
        setChatMessages(prev => [...prev, { role: "assistant", content: `${isAr ? "عذراً، الخدمة غير متاحة حالياً" : "Sorry, the service is currently unavailable"}\n\n${errDetail}` }]);
      }
    } catch {
      setChatMessages(prev => [...prev, { role: "assistant", content: isAr ? "عذراً، حدث خطأ. حاول مرة أخرى" : "Sorry, an error occurred. Try again" }]);
    }
    setChatLoading(false);
  };

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages, chatLoading]);

  const toggleLoudAlerts = () => {
    const newVal = !loudAlerts;
    setLoudAlerts(newVal);
    localStorage.setItem("loud_alerts_seniors", String(newVal));
  };

  const resetForm = () => {
    setFormError(null);
    setName(""); setDosage(""); setFrequency("once"); setTimes(["08:00"]);
    setFoodRelation("any"); setDurationValue(7); setDurationUnit("days");
    setPhotoUrl(null);
    setMedType("pills"); setStockCount(""); setLowStockThreshold("5"); setDoctorNotes("");
  };

  const handleFrequencyChange = (freq: string) => {
    setFrequency(freq);
    const count = FREQUENCY_OPTIONS.find(f => f.value === freq)?.timesCount || 1;
    const defaultTimes = ["08:00", "14:00", "20:00"];
    setTimes(defaultTimes.slice(0, count));
  };

  const handlePhotoUpload = async (file: File) => {
    if (!userId || !file) return;
    setUploadingPhoto(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const fileName = `${userId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("medication-photos")
        .upload(fileName, file);
      if (upErr) throw upErr;
      setPhotoUrl(fileName);
    } catch {
      setFormError(isAr ? "تعذر رفع الصورة. حاول مجددًا أو احفظ بدون صورة." : "Photo upload failed. Retry or save without a photo.");
    }
    setUploadingPhoto(false);
  };

  const handleSave = async () => {
    if (!userId) { onSignInRequest?.(); return; }
    if (savingRef.current || uploadingPhoto || !name.trim() || !dosage.trim()) return;
    if (!validateSchedule(times, durationValue, stockCount, lowStockThreshold)) {
      setFormError(isAr ? "تحقق من المواعيد غير المكررة والمدة والكميات الصحيحة غير السالبة." : "Check unique valid times, duration and non-negative whole stock quantities.");
      return;
    }
    savingRef.current = true; setSaving(true); setFormError(null);
    try {
      const startDate = localDateKey();
      const { data, error } = await supabase.from("medications").insert({
        user_id: userId, name: name.trim(), dosage: dosage.trim(), frequency,
        times: [...times].sort(), food_relation: foodRelation, duration_value: durationValue,
        duration_unit: durationUnit, start_date: startDate,
        end_date: treatmentEnd(startDate, durationValue, durationUnit), photo_url: photoUrl,
        ai_advice: null, interaction_warning: null, is_active: true, medication_type: medType,
        stock_count: stockCount === "" ? null : Number(stockCount),
        low_stock_threshold: lowStockThreshold === "" ? 5 : Number(lowStockThreshold),
        doctor_notes: doctorNotes.trim() || null,
      }).select("*").single();
      if (error || !data) throw error || new Error("Save failed");
      setMedications(prev => [data as Medication, ...prev]); resetForm(); setShowForm(false);
    } catch {
      setFormError(isAr ? "تعذر حفظ الدواء. بقيت بياناتك في النموذج؛ حاول مجددًا." : "Could not save. Your form is preserved; please try again.");
    } finally { savingRef.current = false; setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!userId || !window.confirm(isAr ? "حذف هذا الدواء وسجل جرعاته؟" : "Delete this medication and its dose history?")) return;
    const { error } = await supabase.from("medications").delete().eq("id", id).eq("user_id", userId);
    if (!error) { setMedications(prev => prev.filter(m => m.id !== id)); setDoseLogs(prev => prev.filter(l => l.medication_id !== id)); }
    else setFormError(isAr ? "تعذر حذف الدواء." : "Could not delete medication.");
  };

  const markDoseTaken = async (med: Medication, time: string) => {
    if (!userId || !isTreatmentActive(med) || !med.times.includes(time)) return;
    const scheduledISO = scheduledDate(time).toISOString();
    const key = med.id + scheduledISO;
    if (doseBusy.current.has(key)) return;
    doseBusy.current.add(key); setFormError(null);
    try {
      const { data: existing, error: readError } = await supabase.from("dose_logs")
        .select("*").eq("medication_id", med.id).eq("scheduled_time", scheduledISO).maybeSingle();
      if (readError) throw readError;
      if (existing?.status === "taken") return;
      const result = existing
        ? await supabase.from("dose_logs").update({ status: "taken", taken_at: new Date().toISOString() }).eq("id", existing.id)
        : await supabase.from("dose_logs").insert({ user_id: userId, medication_id: med.id,
            scheduled_time: scheduledISO, status: "taken", taken_at: new Date().toISOString() });
      if (result.error) throw result.error;
      // A dose is not necessarily one tablet/ml. Do not guess a stock decrement.
      await loadMedications();
    } catch { setFormError(isAr ? "تعذر تسجيل الجرعة. حاول مجددًا." : "Could not record this dose. Please retry."); }
    finally { doseBusy.current.delete(key); }
  };

  // Compliance score — last 7 days
  const complianceScore = useMemo(() => {
    if (!doseLogs.length && !medications.length) return 0;
    const totalScheduled = medications.reduce((sum, m) => sum + (isTreatmentActive(m) ? m.times.length : 0), 0) * 1; // today
    if (totalScheduled === 0) return 100;
    const taken = doseLogs.filter(l => l.status === "taken").length;
    return Math.min(100, Math.round((taken / totalScheduled) * 100));
  }, [doseLogs, medications]);

  const todayDosesTotal = useMemo(() =>
    medications.reduce((sum, m) => sum + (isTreatmentActive(m) ? m.times.length : 0), 0), [medications]);
  const todayTaken = useMemo(() => doseLogs.filter(l => l.status === "taken").length, [doseLogs]);
  const todayMissed = useMemo(() => doseLogs.filter(l => l.status === "missed").length, [doseLogs]);

  const handleAddFromAssistant = (medName: string) => {
    setShowAssistant(false);
    setName(medName);
    setShowForm(true);
  };

  return (
    <div className="medication-center space-y-4 max-w-2xl mx-auto" dir={dir}>
      {formError && <p role="alert" className="rounded-xl p-3 bg-red-50 text-red-800">{formError}</p>}
      {/* Medical Disclaimer Banner */}
      <div className="rounded-2xl border-2 p-4 shadow-md" style={{ backgroundColor: "#FFFBEB", borderColor: "#DC2626" }}>
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#FEE2E2" }}>
            <ShieldAlert className="w-6 h-6" style={{ color: "#DC2626" }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`mb-1.5 ${isAr ? "text-right" : "text-left"}`} style={{ color: "#991B1B", fontSize: "16px", fontWeight: 600 }}>
              {isAr ? "⚠️ تنبيه وتخلي مسؤولية طبي قانوني" : "⚠️ Legal & Medical Disclaimer"}
            </h2>
            <p className={`leading-relaxed ${isAr ? "text-right" : "text-left"}`} style={{ color: "#111827", fontSize: "16px", fontWeight: 600 }}>
              {isAr
                ? "هذا القسم مخصص لتسجيل المواعيد وتذكير الجرعات فقط، ولا يقدم أي استشارات طبية أو تشخيصية. يجب عدم تناول أو تعديل أي أدوية إلا بعد استشارة الطبيب المختص أو الصيدلي. التطبيق غير مسؤول عن أي استخدام خاطئ للأدوية."
                : "This section is strictly for dose reminders and schedule tracking. It does not provide medical advice, diagnosis, or treatment. Always consult a qualified physician or healthcare provider before taking or modifying any medication."}
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 p-5 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
            <Pill className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-white">
              {isAr ? "مركز الأدوية" : "Medication Center"}
            </h1>
            <p className="text-sm text-white/80">
              {isAr ? "إدارة أدويتك وتذكيراتك بسهولة" : "Manage your medications and reminders easily"}
            </p>
          </div>
          {/* Loud Alerts Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLoudAlerts}
              aria-pressed={loudAlerts}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold transition-all ${
                loudAlerts ? "bg-amber-400 text-amber-900" : "bg-white/15 text-white"
              }`}
            >
              {loudAlerts ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              <span>{loudAlerts ? (isAr ? "الصوت مفعّل" : "Sound on") : (isAr ? "الصوت مكتوم" : "Sound off")}</span>
            </button>
          </div>
        </div>
        {/* Mode Toggle */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setShowAssistant(false)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all ${
              !showAssistant ? "bg-white text-blue-600" : "bg-white/15 text-white"
            }`}
          >
            <Pill className="w-5 h-5" />
            {isAr ? "أدويتي" : "My Medications"}
          </button>
          <button
            onClick={() => setShowAssistant(true)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all ${
              showAssistant ? "bg-white text-violet-600" : "bg-white/15 text-white"
            }`}
          >
            <Sparkles className="w-5 h-5" />
            {isAr ? "مساعد الأدوية الذكي" : "AI Assistant"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-300 dark:border-slate-600 p-4 space-y-3 text-sm">
        <p>{isAr ? "تذكيرات الويب تعمل ما دامت الصفحة مفتوحة، وقد تتوقف عند قفل الهاتف أو إغلاق المتصفح. استخدم منبّه الهاتف أيضًا." : "Web reminders require this page to stay open and may stop when the phone locks or the browser closes. Also use a phone alarm."}</p>
        <div className="flex flex-wrap gap-2">
          <button className="rounded-lg bg-blue-700 text-white px-3 py-2" onClick={async () => {
            if (!("Notification" in window)) { setNotificationStatus(isAr ? "هذا المتصفح لا يدعم إشعارات الصفحة" : "This browser does not support page notifications"); return; }
            const result = await Notification.requestPermission();
            setNotificationStatus(result === "granted" ? (isAr ? "تم السماح بالإشعارات" : "Notifications allowed") : (isAr ? "لم يُسمح بالإشعارات؛ راجع إعدادات المتصفح" : "Notifications not allowed; check browser settings"));
          }}>{isAr ? "السماح بالإشعارات" : "Allow notifications"}</button>
          <button className="rounded-lg bg-slate-700 text-white px-3 py-2" onClick={() => sendNotification(isAr ? "اختبار تذكير فقط" : "Test reminder only", true)}>{isAr ? "اختبار الصوت" : "Test sound"}</button>
        </div>
        {notificationStatus && <p role="status">{notificationStatus}</p>}
      </div>
      {/* AI Medication Assistant Mode */}
      {showAssistant ? (
        <MedicationAssistant refreshCredits={refreshCredits} lang={lang} dir={dir} onAddToReminder={handleAddFromAssistant} />
      ) : (
      <>

      {/* Add New Medication Button */}
      {!showForm && (
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-bold text-lg shadow-lg transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
        >
          <Plus className="w-6 h-6" />
          {isAr ? "إضافة دواء جديد" : "Add New Medication"}
        </button>
      )}

      {/* Add Form */}
      {showForm && (
        <div className="rounded-2xl bg-white dark:bg-slate-800 border-2 border-blue-200 dark:border-blue-500/30 shadow-lg p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-stone-800 dark:text-slate-100">
              {isAr ? "إضافة دواء جديد" : "Add New Medication"}
            </h2>
            <button aria-label={isAr ? "إغلاق نموذج الدواء" : "Close medication form"} onClick={() => setShowForm(false)} className="w-9 h-9 rounded-full bg-stone-100 dark:bg-slate-700 flex items-center justify-center">
              <X className="w-5 h-5 text-stone-600 dark:text-slate-300" />
            </button>
          </div>

          {/* Photo - at top so user captures image first */}
          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-1 block">
              {isAr ? "صورة علبة الدواء (اختياري)" : "Medication Package Photo (Optional)"}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={e => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); }}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="w-full py-3 rounded-xl border-2 border-dashed border-stone-300 dark:border-slate-600 text-stone-600 dark:text-slate-300 font-bold flex items-center justify-center gap-2 hover:border-blue-400 transition-colors"
            >
              {uploadingPhoto ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
              {photoUrl
                ? (isAr ? "تم اختيار الصورة ✓" : "Photo selected ✓")
                : (isAr ? "التقاط صورة" : "Take Photo")}
            </button>
            {photoUrl && (
              <MedPhoto path={photoUrl} alt="medication" className="mt-2 w-24 h-24 rounded-xl object-cover" />
            )}
          </div>

          {/* Name */}
          {/* Medication Type */}
          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-2 block">
              {isAr ? "نوع الدواء" : "Medication Type"}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {MED_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setMedType(t.value)}
                  className={`py-2.5 px-2 rounded-xl text-xs font-bold border-2 transition-all ${
                    medType === t.value
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-stone-50 dark:bg-slate-700 border-stone-200 dark:border-slate-600 text-stone-700 dark:text-slate-200"
                  }`
                  }
                >
                  {tr(t.ar, t.en, lang)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-1 block">
              {isAr ? "اسم الدواء" : "Medication Name"}
            </label>
            <input
              aria-label={isAr ? "اسم الدواء" : "Medication name"}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={isAr ? "مثال: باراسيتامول" : "e.g. Paracetamol"}
              className="w-full text-base bg-stone-50 dark:bg-slate-700 border-2 border-stone-200 dark:border-slate-600 rounded-xl px-4 py-3 text-stone-800 dark:text-slate-100 outline-none focus:border-blue-500"
            />
          </div>

          {/* Dosage */}
          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-1 block">
              {isAr ? `الجرعة (${getUnitLabel(medType, lang)})` : `Dosage (${getUnitLabel(medType, lang)})`}
            </label>
            <input
              value={dosage}
              onChange={e => setDosage(e.target.value)}
              aria-label={isAr ? "الجرعة حسب الوصفة" : "Dose as prescribed"}
              placeholder={isAr ? "أدخل الجرعة والوحدة حسب الوصفة" : "Enter the dose and unit as prescribed"}
              className="w-full text-base bg-stone-50 dark:bg-slate-700 border-2 border-stone-200 dark:border-slate-600 rounded-xl px-4 py-3 text-stone-800 dark:text-slate-100 outline-none focus:border-blue-500"
            />
          </div>

          {/* Frequency */}
          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-1 block">
              {isAr ? "التكرار" : "Frequency"}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {FREQUENCY_OPTIONS.map(f => (
                <button
                  key={f.value}
                  onClick={() => handleFrequencyChange(f.value)}
                  className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${
                    frequency === f.value
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-stone-50 dark:bg-slate-700 border-stone-200 dark:border-slate-600 text-stone-700 dark:text-slate-200"
                  }`}
                >
                  {tr(f.ar, f.en, lang)}
                </button>
              ))}
            </div>
          </div>

          {/* Times */}
          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-1 block">
              {isAr ? "أوقات الجرعات" : "Dose Times"}
            </label>
            <div className="space-y-2">
              {times.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500 shrink-0" />
                  <input
                    aria-label={isAr ? `موعد الجرعة ${i + 1}` : `Dose time ${i + 1}`}
                    type="time"
                    value={t}
                    onChange={e => {
                      const newTimes = [...times];
                      newTimes[i] = e.target.value;
                      setTimes(newTimes);
                    }}
                    className="flex-1 text-base bg-stone-50 dark:bg-slate-700 border-2 border-stone-200 dark:border-slate-600 rounded-xl px-4 py-2.5 text-stone-800 dark:text-slate-100 outline-none focus:border-blue-500"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Food Relation */}
          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-1 block">
              {isAr ? "علاقة بالطعام" : "Food Relation"}
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {FOOD_OPTIONS.map(f => (
                <button
                  key={f.value}
                  onClick={() => setFoodRelation(f.value)}
                  className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all flex items-center justify-center gap-1.5 ${
                    foodRelation === f.value
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-stone-50 dark:bg-slate-700 border-stone-200 dark:border-slate-600 text-stone-700 dark:text-slate-200"
                  }`}
                >
                  <Utensils className="w-4 h-4" />
                  {tr(f.ar, f.en, lang)}
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-1 block">
              {isAr ? "مدة العلاج" : "Treatment Duration"}
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={durationValue}
                onChange={e => setDurationValue(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 text-base bg-stone-50 dark:bg-slate-700 border-2 border-stone-200 dark:border-slate-600 rounded-xl px-4 py-3 text-stone-800 dark:text-slate-100 outline-none focus:border-blue-500"
              />
              <select
                value={durationUnit}
                onChange={e => setDurationUnit(e.target.value)}
                className="flex-1 text-base bg-stone-50 dark:bg-slate-700 border-2 border-stone-200 dark:border-slate-600 rounded-xl px-4 py-3 text-stone-800 dark:text-slate-100 outline-none focus:border-blue-500"
              >
                {DURATION_UNITS.map(u => (
                  <option key={u.value} value={u.value}>{tr(u.ar, u.en, lang)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Recorded stock (not automatically deducted) */}
          <div className="rounded-xl bg-stone-50 dark:bg-slate-700/50 border-2 border-stone-200 dark:border-slate-600 p-4 space-y-3">
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 flex items-center gap-1.5">
              <Bell className="w-4 h-4 text-blue-600" />
              {isAr ? "المخزون المسجل (لا يُخصم تلقائيًا)" : "Recorded stock (not automatically deducted)"}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-stone-500 dark:text-slate-400 mb-1">
                  {isAr ? "الكمية المتبقية" : "Remaining Stock"}
                </label>
                <input
                  type="number"
                  value={stockCount}
                  onChange={e => setStockCount(e.target.value)}
                  placeholder={isAr ? "مثال: 30" : "e.g. 30"}
                  className="w-full px-3 py-2 rounded-lg border-2 border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-stone-800 dark:text-slate-100 outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-stone-500 dark:text-slate-400 mb-1">
                  {isAr ? "تنبيه عند أقل من" : "Alert when below"}
                </label>
                <input
                  type="number"
                  value={lowStockThreshold}
                  onChange={e => setLowStockThreshold(e.target.value)}
                  placeholder={isAr ? "مثال: 5" : "e.g. 5"}
                  className="w-full px-3 py-2 rounded-lg border-2 border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-stone-800 dark:text-slate-100 outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Doctor's Notes */}
          <div>
            <label className="text-sm font-bold text-stone-700 dark:text-slate-200 mb-1 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-blue-600" />
              {isAr ? "تعليمات خاصة / ملاحظات الطبيب" : "Special Instructions / Doctor's Notes"}
            </label>
            <textarea
              value={doctorNotes}
              onChange={e => setDoctorNotes(e.target.value)}
              placeholder={isAr ? "انسخ تعليمات الطبيب أو الصيدلي هنا." : "Copy your doctor’s or pharmacist’s instructions here."}
              rows={2}
              className="w-full text-sm bg-stone-50 dark:bg-slate-700 border-2 border-stone-200 dark:border-slate-600 rounded-xl px-4 py-2.5 text-stone-800 dark:text-slate-100 outline-none focus:border-blue-500"
            />
          </div>

          <p className="text-sm text-stone-600 dark:text-slate-300">{isAr ? "حفظ الدواء لا يستهلك رصيد ذكاء اصطناعي." : "Saving a medication uses no AI credits."}</p>
          {/* Save */}
          <button
            onClick={handleSave}
            disabled={!name.trim() || !dosage.trim() || saving || uploadingPhoto}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 disabled:opacity-50 text-white font-bold text-lg shadow-lg transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
          >
            {saving || uploadingPhoto ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isAr ? "جاري الحفظ..." : "Saving..."}
              </>
            ) : (
              <>
                <Check className="w-6 h-6" />
                {isAr ? "حفظ الدواء" : "Save Medication"}
              </>
            )}
          </button>
        </div>
      )}

      {/* Daily Summary Card */}
      {medications.length > 0 && (
        <div className="rounded-2xl bg-white dark:bg-slate-800 border-2 border-stone-200 dark:border-slate-700 shadow-md p-5">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-stone-800 dark:text-slate-100">
              {isAr ? "ملخص اليوم" : "Daily Summary"}
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center bg-blue-50 dark:bg-blue-500/10 rounded-xl py-3">
              <p className="text-2xl font-semibold text-blue-600">{todayTaken}</p>
              <p className="text-xs font-bold text-stone-600 dark:text-slate-300">{isAr ? "مأخوذة" : "Taken"}</p>
            </div>
            <div className="text-center bg-amber-50 dark:bg-amber-500/10 rounded-xl py-3">
              <p className="text-2xl font-semibold text-amber-600">{Math.max(0, todayDosesTotal - todayTaken - todayMissed)}</p>
              <p className="text-xs font-bold text-stone-600 dark:text-slate-300">{isAr ? "متبقية" : "Remaining"}</p>
            </div>
            <div className="text-center bg-red-50 dark:bg-red-500/10 rounded-xl py-3">
              <p className="text-2xl font-semibold text-red-600">{todayMissed}</p>
              <p className="text-xs font-bold text-stone-600 dark:text-slate-300">{isAr ? "فائتة" : "Missed"}</p>
            </div>
          </div>
          {/* Compliance Score */}
          <div className="mt-4 flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-500/10 dark:to-green-500/10 rounded-xl p-3">
            <TrendingUp className="w-8 h-8 text-emerald-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-stone-700 dark:text-slate-200">
                {isAr ? "نسبة الجرعات المسجلة اليوم" : "Today’s recorded doses"}
              </p>
              <div className="w-full bg-stone-200 dark:bg-slate-600 rounded-full h-3 mt-1">
                <div
                  className="h-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 transition-all"
                  style={{ width: `${complianceScore}%` }}
                />
              </div>
            </div>
            <span className="text-2xl font-semibold text-emerald-600">{complianceScore}%</span>
          </div>
        </div>
      )}

      {/* Medication Cards */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : medications.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-center">
          <Pill className="w-12 h-12 text-stone-300 mb-2" />
          <p className="text-stone-400 dark:text-slate-500 font-medium">
            {isAr ? "لا توجد أدوية مضافة بعد" : "No medications added yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {medications.map(med => {
            const remaining = getRemainingDays(med.end_date);
            const active = isTreatmentActive(med);
            const foodLabel = FOOD_OPTIONS.find(f => f.value === med.food_relation);
            const medLogs = doseLogs.filter(l => l.medication_id === med.id);
            const takenToday = medLogs.filter(l => l.status === "taken").length;
            return (
              <div key={med.id} className="rounded-2xl bg-white dark:bg-slate-800 border-2 border-stone-200 dark:border-slate-700 shadow-md overflow-hidden">
                {/* Card Header */}
                <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-500/10 dark:to-cyan-500/10">
                  <div className="flex items-start gap-3">
                    {med.photo_url ? (
                      <MedPhoto path={med.photo_url} alt={med.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center shrink-0">
                        <Pill className="w-7 h-7 text-blue-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-stone-800 dark:text-slate-100 truncate">{med.name}</h3>
                      <p className="text-sm font-bold text-blue-600 dark:text-blue-400">{med.dosage}</p>
                    </div>
                    <button
                      onClick={() => handleDelete(med.id)}
                      className="w-9 h-9 rounded-full bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 flex items-center justify-center transition-colors shrink-0"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-3">
                  {/* Info Row */}
                  <div className="flex flex-wrap gap-2">
                    <span className="flex items-center gap-1.5 bg-stone-100 dark:bg-slate-700 rounded-lg px-3 py-1.5 text-sm font-bold text-stone-700 dark:text-slate-200">
                      <Clock className="w-4 h-4 text-blue-500" />
                      {med.times.join(" • ")}
                    </span>
                    <span className="flex items-center gap-1.5 bg-stone-100 dark:bg-slate-700 rounded-lg px-3 py-1.5 text-sm font-bold text-stone-700 dark:text-slate-200">
                      <Utensils className="w-4 h-4 text-orange-500" />
                      {foodLabel ? tr(foodLabel.ar, foodLabel.en, lang) : ""}
                    </span>
                    <span className="flex items-center gap-1.5 bg-stone-100 dark:bg-slate-700 rounded-lg px-3 py-1.5 text-sm font-bold text-stone-700 dark:text-slate-200">
                      <Calendar className="w-4 h-4 text-green-500" />
                      {remaining > 0
                        ? (isAr ? `${remaining} يوم متبقي` : `${remaining} days left`)
                        : (isAr ? "انتهى العلاج" : "Treatment ended")}
                    </span>
                  </div>

                  {/* Low Stock Alert */}
                  {med.stock_count !== null && med.low_stock_threshold !== null && med.stock_count <= med.low_stock_threshold && (
                    <div className="flex items-center gap-2.5 bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-200 dark:border-amber-500/30 rounded-xl p-3">
                      <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                      <p className="text-sm font-bold text-amber-700 dark:text-amber-200">
                        {isAr
                          ? `مخزون منخفض: ${med.stock_count} متبقي`
                          : `Low stock: ${med.stock_count} left`}
                      </p>
                    </div>
                  )}

                  {/* Doctor's Notes */}
                  {med.doctor_notes && (
                    <div className="flex items-start gap-2.5 bg-blue-50 dark:bg-blue-500/10 border-2 border-blue-200 dark:border-blue-500/20 rounded-xl p-3">
                      <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                      <p className="text-sm font-medium text-stone-800 dark:text-slate-100 leading-relaxed">
                        {med.doctor_notes}
                      </p>
                    </div>
                  )}

                  {/* Record the explicitly selected scheduled dose, never an inferred next dose. */}
                  {active && <div className="space-y-2">
                    <p className="text-sm font-medium">{isAr ? "سجّل الجرعة التي أخذتها فعلاً" : "Record the dose you actually took"}</p>
                    {[...med.times].sort().map(time => {
                      const taken = medLogs.some(log => log.scheduled_time === scheduledDate(time).toISOString() && log.status === "taken");
                      return <button key={time} disabled={taken} onClick={() => markDoseTaken(med, time)}
                        className="w-full min-h-11 rounded-xl px-4 py-3 bg-blue-700 text-white disabled:bg-slate-600">
                        <span dir="ltr">{time}</span> — {taken ? (isAr ? "مسجلة كمأخوذة" : "Recorded as taken") : (isAr ? "سجل أنني أخذتها" : "Record as taken")}
                      </button>;
                    })}
                  </div>}

                  {/* AI Advisor */}
                  {med.ai_advice?.advice && med.ai_advice.advice.length > 0 && (
                    <div className="rounded-2xl bg-violet-50 dark:bg-violet-500/10 border-2 border-violet-200 dark:border-violet-500/20 p-4">
                      <div className="flex items-center gap-2.5 mb-3">
                        <div className="w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center shrink-0">
                          <Sparkles className="w-5 h-5 text-violet-600 dark:text-violet-300" />
                        </div>
                        <p className="text-base font-bold text-violet-700 dark:text-violet-200">
                          {isAr ? "المستشار الذكي للأدوية" : "Medication Smart Advisor"}
                        </p>
                      </div>
                      <ul className="space-y-2.5">
                        {med.ai_advice.advice.map((tip, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-violet-500 shrink-0" />
                            <span className="text-base font-medium text-stone-800 dark:text-slate-100 leading-relaxed">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Interaction Warning */}
                  {med.interaction_warning && (
                    <div className="rounded-2xl bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-300 dark:border-amber-500/30 p-4">
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center shrink-0">
                          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <p className="text-base font-bold text-amber-700 dark:text-amber-200">
                          {isAr ? "تحذير تفاعل الأدوية" : "Drug Interaction Warning"}
                        </p>
                      </div>
                      <p className="text-base font-medium text-stone-800 dark:text-slate-100 leading-relaxed">{med.interaction_warning}</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-2 font-bold">
                        {isAr ? "⚠️ هذا ليس تشخيصاً طبياً — استشر طبيبك" : "⚠️ This is NOT a medical diagnosis — consult your doctor"}
                      </p>
                    </div>
                  )}

                  {/* Today's progress */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-bold text-stone-600 dark:text-slate-300">
                      {isAr ? `جرعات اليوم: ${takenToday}/${med.times.length}` : `Today: ${takenToday}/${med.times.length}`}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      {medications.length > 0 && (
        <p className="text-xs text-stone-400 dark:text-slate-500 text-center px-4">
          {isAr
            ? "مركز الأدوية لأغراض تنظيمية فقط ولا يغني عن الاستشارة الطبية"
            : "Medication Center is for organizational purposes only and does not replace medical advice"}
        </p>
      )}

      </>
      )}

      {/* AI Chat Panel */}
      {showChat && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setShowChat(false)}>
          <div
            className="w-full sm:max-w-md bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[80vh]"
            onClick={e => e.stopPropagation()}
            dir={dir}
          >
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 border-b border-stone-200 dark:border-slate-700 bg-gradient-to-r from-violet-600 to-blue-600 rounded-t-3xl sm:rounded-t-3xl">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{isAr ? "مساعد الأدوية الذكي" : "Medication AI Assistant"}</h3>
                <p className="text-xs text-white/70">{isAr ? "اسألني عن أدويتك" : "Ask me about your medications"}</p>
              </div>
              <button onClick={() => setShowChat(false)} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Chat Messages */}
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[50vh]">
              {chatMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Bot className="w-12 h-12 text-violet-400 mb-2" />
                  <p className="text-stone-500 dark:text-slate-400 font-medium text-sm">
                    {isAr ? "مرحباً! اسألني أي سؤال عن أدويتك" : "Hi! Ask me any question about your medications"}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 justify-center">
                    {[
                      isAr ? "متى آخذ دوائي؟" : "When should I take my meds?",
                      isAr ? "هل هناك تفاعلات؟" : "Are there interactions?",
                      isAr ? "ما هي الآثار الجانبية؟" : "What are side effects?",
                    ].map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setChatInput(s)}
                        className="px-3 py-1.5 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-300 text-xs font-bold border border-violet-200 dark:border-violet-500/20 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((msg, i) => {
                const isUser = msg.role === "user";
                const isError = !isUser && msg.content.startsWith(isAr ? "عذراً" : "Sorry");
                const isWarn = !isUser && (msg.content.includes(isAr ? "تحذير" : "warning") || msg.content.includes(isAr ? "تفاعل" : "interaction"));
                const lines = msg.content.split("\n").filter(l => l.trim());
                const isList = lines.length > 1;
                return (
                  <div key={i} className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
                    <div className={`w-10 h-10 rounded-full shrink-0 flex items-center justify-center shadow-sm ${
                      isUser ? "bg-blue-100 dark:bg-blue-500/20" : isError ? "bg-red-100 dark:bg-red-500/20" : isWarn ? "bg-amber-100 dark:bg-amber-500/20" : "bg-violet-100 dark:bg-violet-500/20"
                    }`}>
                      {isUser
                        ? <User className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                        : isError
                          ? <AlertCircle className="w-5 h-5 text-red-500" />
                          : isWarn
                            ? <AlertTriangle className="w-5 h-5 text-amber-500" />
                            : <Bot className="w-5 h-5 text-violet-600 dark:text-violet-300" />}
                    </div>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 leading-relaxed ${
                      isUser
                        ? "bg-blue-600 text-white rounded-tr-sm"
                        : isError
                          ? "bg-red-50 dark:bg-red-500/10 border-2 border-red-200 dark:border-red-500/30 text-red-800 dark:text-red-200 rounded-tl-sm"
                          : isWarn
                            ? "bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-200 dark:border-amber-500/30 text-amber-900 dark:text-amber-100 rounded-tl-sm"
                            : "bg-violet-50 dark:bg-violet-500/10 border-2 border-violet-200 dark:border-violet-500/30 text-stone-800 dark:text-slate-100 rounded-tl-sm"
                    }`}>
                      {isList && !isUser ? (
                        <ul className="space-y-2">
                          {lines.map((line, idx) => {
                            const cleanLine = line.replace(/^[-•*\d+.\)]+\s*/, "").trim();
                            if (!cleanLine) return null;
                            return (
                              <li key={idx} className="flex items-start gap-2.5">
                                <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${
                                  isError ? "bg-red-400" : isWarn ? "bg-amber-400" : "bg-violet-400"
                                }`} />
                                <span className="text-base font-medium">{cleanLine}</span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-base font-medium whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                );
              })}
              {chatLoading && (
                <div className="flex gap-2.5">
                  <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center bg-violet-100 dark:bg-violet-500/20">
                    <Bot className="w-5 h-5 text-violet-600 dark:text-violet-300" />
                  </div>
                  <div className="bg-violet-50 dark:bg-violet-500/10 border-2 border-violet-200 dark:border-violet-500/30 rounded-2xl rounded-tl-sm px-5 py-4 flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="p-3 border-t border-stone-200 dark:border-slate-700 flex gap-2">
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") sendChatMessage(); }}
                placeholder={isAr ? "اكتب سؤالك هنا..." : "Type your question here..."}
                className="flex-1 bg-stone-50 dark:bg-slate-700 border-2 border-stone-200 dark:border-slate-600 rounded-xl px-4 py-3.5 text-stone-800 dark:text-slate-100 outline-none focus:border-violet-500 text-base"
              />
              <button
                onClick={sendChatMessage}
                disabled={!chatInput.trim() || chatLoading}
                className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 disabled:opacity-50 text-white flex items-center justify-center transition-all active:scale-95 shrink-0"
              >
                {chatLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function sendNotification(message: string, loud: boolean) {
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      const notif = new Notification("💊 " + message, {
        body: loud ? "⚠️ " + message : message,
        tag: message,
      });
      setTimeout(() => notif.close(), 10000);
    }
    // Vibration
    if ("vibrate" in navigator) {
      navigator.vibrate(loud ? [300, 100, 300, 100, 300] : [200]);
    }
    // Sound
    if (loud) {
      try {
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
      gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
        void ctx.resume();
        osc.onended = () => { void ctx.close(); };
        osc.start();
        osc.stop(ctx.currentTime + 0.5);
      } catch {}
    }
  } catch {}
}
